// httpPorts — the Snapshot BC slice: the state-snapshot save/load flow (VCR
// replacement, IDD f2469548). Both legs are impure DOM/network I/O isolated to
// this port, mirroring `packaging.downloadExport`.

import { Result } from "@bilda/utils";
import { API_BASE, postSnapshot } from "../httpClient.js";
import type { SnapshotPorts } from "../ports/snapshot.js";

// Trigger the browser's attachment download of the current state snapshot. The
// `GET /snapshot` response carries `Content-Disposition: attachment`, so a
// synthetic anchor click saves the file WITHOUT navigating away — the same
// mechanism the package-export download uses.
const saveSnapshot = (): void => {
    const a = document.createElement("a");
    a.href = `${API_BASE}/snapshot`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
};

// Open a file picker, read the chosen `.snapshot.json`, POST it to load, then
// reload so the app re-projects from the seeded state (the client holds no
// domain state — a fresh boot reads the seeded `/state`). A malformed file is
// refused server-side with a typed error; we surface it plainly rather than
// silently succeeding. Impure DOM+network I/O, all isolated to this port.
const loadSnapshot = (): void => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json,.json";
    input.style.display = "none";
    input.onchange = async () => {
        const file = input.files?.[0];
        input.remove();
        if (file === undefined) return;
        try {
            const text = await file.text();
            const r = await postSnapshot(text);
            if (Result.isErr(r)) {
                // Power-user load action; the port has no notice channel, and
                // a silent failure would leave the workspace unchanged with no
                // signal. The server already refused with a typed error.
                window.alert(
                    "Could not load that snapshot. The file may be invalid or from an incompatible version.",
                );
                return;
            }
            // Re-project: a full boot is the simplest, most robust way to
            // reflect the seeded state given the client folds nothing locally.
            window.location.reload();
        } catch {
            window.alert("Could not read that file.");
        }
    };
    document.body.appendChild(input);
    input.click();
};

export const snapshotHttp: SnapshotPorts = {
    saveSnapshot,
    loadSnapshot,
};
